import classNames from 'classnames';
import {useRouter} from 'next/router';
import React, {useState} from 'react';

import {GwgAccelerator} from '@udemy/career-academies';
import {useMatchMedia} from '@udemy/hooks';
import {useI18n} from '@udemy/i18n';
import {LectureStackRecommendationUnitSkeleton} from '@udemy/react-browse-lecture';
import {
    OnePaneLayoutContainer,
    OnePaneLayoutFullWidthSection,
} from '@udemy/react-layout-components';
import {UserOccupationGoalHeader} from '@udemy/react-personalized-content';
import {HardOptInModal} from '@udemy/react-subscription-experience';

import {ErrorBoundary} from 'src/components/error-boundary/error-boundary.react-component';
import {RetentionPromoClaimAlert} from 'src/components/retention-promo-claim-alert/retention-promo-claim-alert.react-component';
import {useUserOccupationInfoQuery} from 'src/generated/graphql';
import {useLocalStorageDismiss} from 'src/hooks/use-local-storage-dismiss';
import {SubscriptionEnrollment, type UserOccupationInfo} from 'src/lib/fetchers';
import {
    calculateEnableInProductOccupationCollection,
    isTechLearner,
} from 'src/lib/occupation-helpers';
import {UserEnrollmentsData} from 'src/types/page-props';

import {COURSE_RECOMMENDATIONS_SECTION_ID} from '../constants';
import {AssessmentsBanner} from './components/assessments-banner/assessments-banner.react-component';
import {AssessmentsDiscoveryUnit} from './components/assessments-discovery-unit.react-component';
import {AtRiskFeedbackModal} from './components/at-risk-feedback-modal/at-risk-feedback-modal.react-component';
import {CertificationInterestDiscoveryUnit} from './components/certification-interest-discovery-unit.react-component';
import {DiscoveryUnitsSection} from './components/discovery-units-section.react-component';
import {
    GwgBannerPPHP,
    GwgCarouselPPHP,
    GwgSubscriptionProvider,
} from './components/gwg-section-pphp.react-component';
import {LecturesDiscoveryUnit} from './components/lectures-discovery-unit.react-component';
import {MyLearningUnitSection} from './components/my-learning-unit-section.react-component';
import {OccupationCollectionBannerComponent} from './components/occupation-collection-banner.react-component';
import {PersonalPlanBanner} from './components/personal-plan-banner.react-component';
import {PersonalPlanChecklistModals} from './components/personal-plan-checklist-modals.react-component';
import {PopQuizModal} from './components/pop-quiz-modal.react-component';
import {SetCareerGoalUnitComponent} from './components/set-career-goal-unit.react-component';
import {StreaksUnit} from './components/streaks-unit.react-component';
import {ValuePlanBanner} from './components/value-plan-banner.react-component';
import {ValuePlanUpsellBanner} from './components/value-plan-upsell/value-plan-upsell-banner.react-component';
import {YearOneRecapSection} from './components/year-one-recap-section.react-component';
import {useAtRiskSurveyTrigger} from './hooks/use-at-risk-survey-trigger';
import {PersonalPlanHomePageProps} from './personal-plan-home-page';
import styles from './personal-plan-home-page.module.less';

const ASSESSMENTS_BANNER_DISMISSED_KEY = 'assessments_banner_dismissed';

interface PersonalPlanHomePageFeatureProps extends PersonalPlanHomePageProps {
    userEnrollments?: UserEnrollmentsData;
    subscriptionEnrollment?: SubscriptionEnrollment;
    accelerator?: GwgAccelerator;
}

export const PersonalPlanHomePageFeature = (props: PersonalPlanHomePageFeatureProps) => {
    const {
        accelerator,
        enableAssessmentsBannerPP,
        enableAssessmentsInPersonalPlan,
        topicInterestIds,
        userHasCertificationInterests,
        userHasSkillInterests,
        enableCertificationBasedCourseUnitPP,
        enableLabsInPersonalPlan,
        enableNewCertificationBasedCourseUnitUIPP,
        enableNewSkillBasedCourseUnitUIPP,
        enablePopQuizPP,
        popQuizV2ExperienceEnabled,
        enableSkillBasedCourseUnitPP,
        enableStreaksInPersonalPlan,
        isOccupationCollectionPromptDismissed,
        isPaidSubscriber,
        isValuePlanSubscriber,
        showVerticalScroll,
        useCatalogCourseCards,
        showAtRiskFeedbackSurvey,
        subscriptionStartDate,
        subscriberEngagementRecapData,
        subscriptionEnrollment,
        userHasUBProAccess,
        userEnrollments,
    } = props;

    const {data: occupationData, isLoading: isOccupationLoading} = useUserOccupationInfoQuery();
    const userOccupationInfo = (occupationData?.userOccupationInfo as UserOccupationInfo) ?? null;

    const userIsTechLearner = isTechLearner(userOccupationInfo);
    const enableInProductOccupationCollection = calculateEnableInProductOccupationCollection(
        isPaidSubscriber,
        userOccupationInfo,
    );

    const {gettext} = useI18n();

    const [isChecklistFlowOpen, setIsChecklistFlowOpen] = useState(false);

    const [isBannerDismissed, handleBannerDismiss] = useLocalStorageDismiss(
        ASSESSMENTS_BANNER_DISMISSED_KEY,
    );

    const {query} = useRouter();
    const forceShowBanner = 'showAssessmentsBanner' in query;
    const showAssessmentsBanner =
        forceShowBanner || (enableAssessmentsBannerPP && !isBannerDismissed);

    const {
        isEligibleForAtRiskSurvey,
        openAtRiskModal,
        isAtRiskModalOpen,
        closeAtRiskModal,
        lastCourseUrl,
        lastCourseId,
        handleSurveyLoaded,
    } = useAtRiskSurveyTrigger({showAtRiskFeedbackSurvey, userEnrollments});

    const isMobileMax = useMatchMedia('mobile-max');
    const isMobile = !!isMobileMax;
    const deviceType = isMobile ? 'mobile' : 'desktop';

    // Show the lectures unit whenever the user has any occupation context — group, canonical, or
    // raw — mirroring the monolith's occupationGroupName check.
    const hasOccupationGroupName =
        !isOccupationLoading &&
        Boolean(
            userOccupationInfo?.group?.name ||
                userOccupationInfo?.canonical?.id ||
                userOccupationInfo?.raw?.id,
        );

    return (
        <OnePaneLayoutContainer>
            <RetentionPromoClaimAlert />
            <HardOptInModal />
            <OnePaneLayoutFullWidthSection>
                <div id="main-content-anchor">
                    <UserOccupationGoalHeader
                        {...(userOccupationInfo ? {userOccupationInfo} : {})}
                        showBackground
                    />
                </div>
                <GwgSubscriptionProvider>
                    <div className="ud-container ud-page-wrapper">
                        {showAssessmentsBanner && (
                            <ErrorBoundary>
                                <AssessmentsBanner
                                    onDismiss={handleBannerDismiss}
                                    topicInterestIds={topicInterestIds}
                                    hasCertInterests={userHasCertificationInterests}
                                    hasSkillInterests={userHasSkillInterests}
                                    className="component-margin"
                                    forceShow={forceShowBanner}
                                />
                            </ErrorBoundary>
                        )}

                        {subscriberEngagementRecapData && (
                            <YearOneRecapSection
                                subscriberEngagementRecapData={subscriberEngagementRecapData}
                            />
                        )}

                        {enableStreaksInPersonalPlan && (
                            <StreaksUnit className="component-margin" />
                        )}

                        {isValuePlanSubscriber && (
                            <ValuePlanUpsellBanner
                                isPaidSubscriber={isPaidSubscriber}
                                subscriptionEnrollment={subscriptionEnrollment}
                            />
                        )}

                        {!isValuePlanSubscriber && (
                            <ErrorBoundary>
                                <GwgBannerPPHP className="component-margin" />
                            </ErrorBoundary>
                        )}

                        <MyLearningUnitSection
                            isPaidSubscriber={isPaidSubscriber}
                            userEnrollments={userEnrollments}
                            enableLabsInPersonalPlan={enableLabsInPersonalPlan}
                            enableAssessmentsInPersonalPlan={enableAssessmentsInPersonalPlan}
                            userHasUBProAccess={userHasUBProAccess}
                            onOpenAtRiskModal={openAtRiskModal}
                        />

                        <CertificationInterestDiscoveryUnit
                            enableCertificationBasedCourseUnitPP={
                                enableCertificationBasedCourseUnitPP
                            }
                            enableNewCertificationBasedCourseUnitUIPP={
                                enableNewCertificationBasedCourseUnitUIPP
                            }
                            deviceType={deviceType}
                            isMobile={isMobile}
                            className="component-margin"
                        />

                        {!isOccupationLoading &&
                            enableInProductOccupationCollection &&
                            !isOccupationCollectionPromptDismissed && (
                                <OccupationCollectionBannerComponent
                                    enableInProductOccupationCollection={
                                        enableInProductOccupationCollection
                                    }
                                    isOccupationCollectionPromptDismissed={
                                        isOccupationCollectionPromptDismissed
                                    }
                                    className="component-margin"
                                />
                            )}

                        {isOccupationLoading ? (
                            <div
                                className="component-margin"
                                data-purpose="lectures-on-landing-container"
                            >
                                <LectureStackRecommendationUnitSkeleton
                                    representativeTopicName=""
                                    isStandaloneUnit={true}
                                />
                            </div>
                        ) : hasOccupationGroupName ? (
                            <LecturesDiscoveryUnit
                                deviceType={deviceType}
                                userOccupationInfo={userOccupationInfo}
                                className="component-margin"
                            />
                        ) : (
                            <SetCareerGoalUnitComponent
                                enableInProductOccupationCollection={
                                    enableInProductOccupationCollection
                                }
                                userOccupationInfo={userOccupationInfo}
                                className="component-margin"
                            />
                        )}

                        {!showAssessmentsBanner && (
                            <AssessmentsDiscoveryUnit
                                enableAssessmentsInPersonalPlan={enableAssessmentsInPersonalPlan}
                                isTechLearner={userIsTechLearner}
                                deviceType={deviceType}
                                className="component-margin"
                            />
                        )}

                        <div id={COURSE_RECOMMENDATIONS_SECTION_ID} className="component-margin">
                            <h2
                                className={classNames(
                                    'ud-heading-serif-xxl',
                                    styles['section-title'],
                                )}
                                data-purpose="what-to-learn-next-title"
                            >
                                {gettext('Explore the collection')}
                            </h2>
                            {isValuePlanSubscriber && isPaidSubscriber ? (
                                <ValuePlanBanner />
                            ) : (
                                <PersonalPlanBanner isPaidSubscriber={isPaidSubscriber} />
                            )}
                            <ErrorBoundary>
                                <GwgCarouselPPHP
                                    accelerator={accelerator}
                                    className="component-margin"
                                />
                            </ErrorBoundary>
                            <DiscoveryUnitsSection
                                className="component-margin"
                                enableSkillBasedCourseUnitPP={enableSkillBasedCourseUnitPP}
                                enableNewSkillBasedCourseUnitUIPP={
                                    enableNewSkillBasedCourseUnitUIPP
                                }
                                enableLabsInPersonalPlan={enableLabsInPersonalPlan}
                                isTechLearner={userIsTechLearner}
                                deviceType={deviceType}
                                isMobile={isMobile}
                                showVerticalScroll={showVerticalScroll}
                                useCatalogCourseCards={useCatalogCourseCards}
                            />
                        </div>
                    </div>
                </GwgSubscriptionProvider>
            </OnePaneLayoutFullWidthSection>
            <PersonalPlanChecklistModals
                isPaidSubscriber={isPaidSubscriber}
                subscriptionStartDate={subscriptionStartDate}
                onModalOpenStateChange={setIsChecklistFlowOpen}
            />
            {isEligibleForAtRiskSurvey && (
                <AtRiskFeedbackModal
                    isOpen={isAtRiskModalOpen}
                    onClose={closeAtRiskModal}
                    surveyCode="at-risk-feedback"
                    lastCourseUrl={lastCourseUrl}
                    lastCourseId={lastCourseId}
                    source="jbi"
                    onSurveyLoaded={handleSurveyLoaded}
                />
            )}
            <ErrorBoundary>
                <PopQuizModal
                    enablePopQuizPP={enablePopQuizPP}
                    popQuizV2ExperienceEnabled={popQuizV2ExperienceEnabled}
                    areOtherModalsOpen={isChecklistFlowOpen}
                />
            </ErrorBoundary>
        </OnePaneLayoutContainer>
    );
};
